import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Paper, Grid, TextField, Button, Divider,
    Alert, Snackbar, Slider, Select, MenuItem, FormControl, InputLabel,
    Switch, FormControlLabel, Stack, List, ListItem, ListItemText, ListItemButton,
    IconButton, Tooltip, Avatar, CircularProgress, Chip
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon, Close as CloseIcon, Brush as BrushIcon } from '@mui/icons-material';
import { doc, setDoc, deleteDoc, collection, onSnapshot, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import PageLayout from '../components/PageLayout';
import { ThemeDefinition, DEFAULT_THEME_DEF, ThemeAsset, useTheme } from '../context/ThemeContext';
import ResponsiveTitle from '../components/ResponsiveTitle';

const ThemeConfig = () => {
    const { activeThemeDefinition } = useTheme();
    const [themes, setThemes] = useState<ThemeDefinition[]>([]);
    const [selectedThemeId, setSelectedThemeId] = useState<string>('default');
    const [currentConfig, setCurrentConfig] = useState<ThemeDefinition>(DEFAULT_THEME_DEF);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [saving, setSaving] = useState(false);

    // Charger la liste des thèmes
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'themes'), (snapshot) => {
            const loadedThemes: ThemeDefinition[] = [];
            snapshot.forEach((doc) => {
                loadedThemes.push({ id: doc.id, ...doc.data() } as ThemeDefinition);
            });

            // Trier: Default d'abord, puis par date de début
            loadedThemes.sort((a, b) => {
                if (a.isDefault) return -1;
                if (b.isDefault) return 1;
                return (a.startDate || '').localeCompare(b.startDate || '');
            });

            setThemes(loadedThemes);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    // Mettre à jour la config courante quand on change de sélection ou quand la liste change
    useEffect(() => {
        const theme = themes.find(t => t.id === selectedThemeId);
        if (theme) {
            setCurrentConfig({
                ...theme,
                threeJs: {
                    enabled: theme.threeJs?.enabled !== undefined ? theme.threeJs.enabled : true,
                    style: theme.threeJs?.style || 'both',
                    waveSpeed: theme.threeJs?.waveSpeed !== undefined ? theme.threeJs.waveSpeed : 0.5,
                    particleCount: theme.threeJs?.particleCount !== undefined ? theme.threeJs.particleCount : 800,
                }
            });
        }
    }, [selectedThemeId, themes]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'themes', currentConfig.id), currentConfig);
            setMessage({ type: 'success', text: "Thème sauvegardé avec succès !" });
        } catch (error) {
            console.error("Erreur sauvegarde:", error);
            setMessage({ type: 'error', text: "Erreur lors de la sauvegarde." });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateTheme = async () => {
        try {
            // Créer un nouveau thème basé sur le défaut mais sans l'ID
            const newThemeBase = {
                ...DEFAULT_THEME_DEF,
                id: '', // Sera remplacé
                name: 'Nouveau Thème',
                isDefault: false,
                startDate: new Date().toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                assets: { logos: [], impacts: [] } // Vider les assets pour éviter doublons
            };

            // Ajouter dans Firestore pour avoir un ID
            const docRef = await addDoc(collection(db, 'themes'), { ...newThemeBase });

            // Mettre à jour l'objet avec son ID
            await setDoc(docRef, { ...newThemeBase, id: docRef.id });

            setSelectedThemeId(docRef.id);
            setMessage({ type: 'success', text: "Nouveau thème créé !" });
        } catch (e) {
            console.error(e);
            setMessage({ type: 'error', text: "Erreur création thème." });
        }
    };

    const handleDeleteTheme = async (themeId: string) => {
        if (themeId === 'default') return;
        if (!window.confirm("Supprimer ce thème ?")) return;

        try {
            await deleteDoc(doc(db, 'themes', themeId));
            setSelectedThemeId('default');
            setMessage({ type: 'success', text: "Thème supprimé." });
        } catch (e) {
            setMessage({ type: 'error', text: "Erreur suppression." });
        }
    };

    // Handlers pour la config
    const handleChange = (path: string[], value: any) => {
        setCurrentConfig(prev => {
            const newConfig = { ...prev };
            let current: any = newConfig;
            for (let i = 0; i < path.length - 1; i++) {
                if (!current[path[i]]) current[path[i]] = {};
                current = current[path[i]];
            }
            current[path[path.length - 1]] = value;
            return newConfig;
        });
    };

    const handleAssetUpload = async (file: File, type: 'logo' | 'cursor' | 'impact' | 'watermark') => {
        if (!file) return;

        if (!currentConfig.id) {
            setMessage({ type: 'error', text: "Erreur critique : ID du thème introuvable. Essayez de rafraîchir la page." });
            return;
        }

        const storagePath = `themes/${currentConfig.id}/${type}s/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);

        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            const newAsset: ThemeAsset = { url, name: file.name, path: storagePath };

            // Calculer la nouvelle configuration
            const newAssets = { ...(currentConfig.assets || { logos: [], impacts: [] }) };
            if (type === 'logo') {
                newAssets.logos = [...(newAssets.logos || []), newAsset];
            } else if (type === 'impact') {
                newAssets.impacts = [...(newAssets.impacts || []), newAsset];
            } else if (type === 'cursor') {
                newAssets.cursor = newAsset;
            } else if (type === 'watermark') {
                newAssets.watermark = newAsset;
            }

            const updatedConfig = { ...currentConfig, assets: newAssets };

            // 1. Mettre à jour l'état local
            setCurrentConfig(updatedConfig);

            // 2. Sauvegarder immédiatement dans Firestore
            await setDoc(doc(db, 'themes', currentConfig.id), updatedConfig);

            setMessage({ type: 'success', text: "Image uploadée et thème sauvegardé !" });
        } catch (e: any) {
            console.error("Erreur détaillée upload:", e);
            // Afficher un message plus précis selon le code d'erreur si possible
            let errorMsg = "Erreur lors de l'upload.";
            if (e.code === 'storage/unauthorized') {
                errorMsg = "Erreur de permission : Vous n'avez pas les droits pour envoyer des fichiers.";
            } else if (e.message) {
                errorMsg = `Erreur upload: ${e.message}`;
            }
            setMessage({ type: 'error', text: errorMsg });
        }
    };

    const removeAsset = async (asset: ThemeAsset, type: 'logo' | 'cursor' | 'impact' | 'watermark') => {
        if (!window.confirm("Voulez-vous vraiment supprimer définitivement cette image ?")) return;

        try {
            // 1. Suppression physique du fichier dans le Storage
            if (asset.path) {
                const fileRef = ref(storage, asset.path);
                await deleteObject(fileRef);
            } else {
                console.warn("Pas de chemin de stockage (path) trouvé pour cet asset. Suppression de la référence uniquement.");
            }

            // 2. Calculer la nouvelle configuration
            const newAssets = { ...(currentConfig.assets || { logos: [], impacts: [] }) };

            if (type === 'logo') {
                const currentLogos = newAssets.logos || [];
                newAssets.logos = currentLogos.filter(a => a.url !== asset.url);
            } else if (type === 'impact') {
                const currentImpacts = newAssets.impacts || [];
                newAssets.impacts = currentImpacts.filter(a => a.url !== asset.url);
            } else if (type === 'cursor') {
                delete newAssets.cursor;
            } else if (type === 'watermark') {
                delete newAssets.watermark;
            }

            const updatedConfig = { ...currentConfig, assets: newAssets };

            // 3. Mettre à jour l'état local
            setCurrentConfig(updatedConfig);

            // 4. Sauvegarder dans Firestore
            await setDoc(doc(db, 'themes', currentConfig.id), updatedConfig);
            setMessage({ type: 'success', text: "Fichier supprimé du stockage et référence retirée." });
        } catch (e: any) {
            console.error("Erreur suppression asset:", e);
            setMessage({ type: 'error', text: "Erreur lors de la suppression: " + (e.message || "Inconnue") });
        }
    };

    const handleToggleWatermark = async (enabled: boolean) => {
        // Calculer la nouvelle config
        const newAssets = { ...(currentConfig.assets || { logos: [], impacts: [] }), watermarkEnabled: enabled };
        const updatedConfig = { ...currentConfig, assets: newAssets };

        // Mise à jour locale
        setCurrentConfig(updatedConfig);

        // Sauvegarde immédiate
        try {
            await setDoc(doc(db, 'themes', currentConfig.id), updatedConfig);
            // Feedback discret ou pas de feedback pour fluidité
        } catch (e) {
            console.error("Erreur sauvegarde toggle watermark:", e);
            setMessage({ type: 'error', text: "Erreur lors de la mise à jour du filigrane." });
        }
    };


    if (loading) return <Box sx={{ p: 4, color: 'white' }}>Chargement...</Box>;

    return (
        <PageLayout containerWidth="xl">
            <Container maxWidth={false} sx={{ mt: 4 }}>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: 4,
                    px: 3,
                    py: 1,
                    color: 'white',
                    width: 'fit-content'
                }}>
                    <BrushIcon sx={{ color: 'white' }} />
                    <ResponsiveTitle variant="h4" sx={{ color: 'white' }}>
                        Configuration du Thème
                    </ResponsiveTitle>
                </Box>

                <Grid container spacing={3}>

                    {/* GAUCHE : LISTE DES THÈMES */}
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, height: '100%', backgroundColor: 'rgba(23, 42, 69, 0.9)', color: 'white' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                                <Typography variant="h6">Thèmes</Typography>
                                <IconButton onClick={handleCreateTheme} sx={{ color: '#4a90e2' }}><AddIcon /></IconButton>
                            </Box>
                            <List>
                                {themes.map(theme => {
                                    const isActive = activeThemeDefinition?.id === theme.id;
                                    const isSelected = selectedThemeId === theme.id;

                                    return (
                                        <Paper
                                            key={theme.id}
                                            elevation={isSelected ? 10 : 1}
                                            sx={{
                                                mb: 2,
                                                backgroundColor: isSelected ? 'rgba(74, 144, 226, 0.2)' : (isActive ? 'rgba(46, 125, 50, 0.15)' : 'rgba(0,0,0,0.2)'),
                                                border: isSelected ? '1px solid #4a90e2' : (isActive ? '1px solid #4caf50' : '1px solid transparent'),
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <ListItem
                                                secondaryAction={!theme.isDefault && (
                                                    <IconButton edge="end" onClick={() => handleDeleteTheme(theme.id)}>
                                                        <DeleteIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                                    </IconButton>
                                                )}
                                                disablePadding
                                            >
                                                <ListItemButton onClick={() => setSelectedThemeId(theme.id)}>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {theme.name}
                                                                {isActive && <Chip label="ACTIF" color="success" size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 'bold' }} />}
                                                            </Box>
                                                        }
                                                        secondary={theme.isDefault ? "Par défaut" : `${theme.startDate?.split('-').reverse().slice(0, 2).join('/')} au ${theme.endDate?.split('-').reverse().slice(0, 2).join('/')} (Annuel)`}
                                                        primaryTypographyProps={{ fontWeight: 'bold', color: 'white' }}
                                                        secondaryTypographyProps={{ color: '#aaa', fontSize: '0.8rem' }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        </Paper>
                                    )
                                })}
                            </List>
                        </Paper>
                    </Grid>

                    {/* MILIEU : CONFIG COULEURS */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 3, backgroundColor: 'rgba(23, 42, 69, 0.9)', color: 'white', maxHeight: '85vh', overflowY: 'auto' }}>
                            <Typography variant="h6" sx={{ mb: 3, color: '#64a6ff' }}>Configuration des Couleurs</Typography>

                            <TextField
                                label="Nom du thème"
                                fullWidth
                                value={currentConfig.name}
                                onChange={(e) => handleChange(['name'], e.target.value)}
                                disabled={currentConfig.isDefault}
                                sx={{ mb: 3 }}
                            />

                            {!currentConfig.isDefault && (
                                <Box sx={{ mb: 3 }}>
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            label="Date de début"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={currentConfig.startDate}
                                            onChange={(e) => handleChange(['startDate'], e.target.value)}
                                        />
                                        <TextField
                                            label="Date de fin"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={currentConfig.endDate}
                                            onChange={(e) => handleChange(['endDate'], e.target.value)}
                                        />
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: '#aaa', mt: 1, display: 'block' }}>
                                        * L'année est ignorée. Ce thème s'activera chaque année à cette période (ex: Noël, Halloween).
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                            {/* CONFIGURATION DES COULEURS */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ color: '#64a6ff' }}>1. Fond du site</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentConfig.colors.background.mode === 'gradient'}
                                            onChange={(e) => handleChange(['colors', 'background', 'mode'], e.target.checked ? 'gradient' : 'solid')}
                                        />
                                    }
                                    label="Dégradé"
                                    sx={{ color: 'white' }}
                                />
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth label="Couleur 1" value={currentConfig.colors.background.color1}
                                        onChange={(e) => handleChange(['colors', 'background', 'color1'], e.target.value)}
                                        InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.background.color1} onChange={(e) => handleChange(['colors', 'background', 'color1'], e.target.value)} style={{ width: 30, height: 30, border: 'none', marginRight: 10, cursor: 'pointer' }} /> }}
                                    />
                                </Grid>
                                {currentConfig.colors.background.mode === 'gradient' && (
                                    <>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth label="Couleur 2" value={currentConfig.colors.background.color2}
                                                onChange={(e) => handleChange(['colors', 'background', 'color2'], e.target.value)}
                                                InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.background.color2} onChange={(e) => handleChange(['colors', 'background', 'color2'], e.target.value)} style={{ width: 30, height: 30, border: 'none', marginRight: 10, cursor: 'pointer' }} /> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select value={currentConfig.colors.background.gradientType} label="Type" onChange={(e) => handleChange(['colors', 'background', 'gradientType'], e.target.value)}>
                                                    <MenuItem value="linear">Linéaire</MenuItem>
                                                    <MenuItem value="radial">Radial</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {currentConfig.colors.background.gradientType === 'linear' && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption">Angle: {currentConfig.colors.background.angle}°</Typography>
                                                <Slider value={currentConfig.colors.background.angle} min={0} max={360} onChange={(_, v) => handleChange(['colors', 'background', 'angle'], v)} />
                                            </Grid>
                                        )}
                                    </>
                                )}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            height: 60, width: '100%', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)',
                                            background: currentConfig.colors.background.mode === 'solid'
                                                ? currentConfig.colors.background.color1
                                                : currentConfig.colors.background.gradientType === 'radial'
                                                    ? `radial-gradient(circle, ${currentConfig.colors.background.color1} ${currentConfig.colors.background.stop1}%, ${currentConfig.colors.background.color2} ${currentConfig.colors.background.stop2}%)`
                                                    : `linear-gradient(${currentConfig.colors.background.angle}deg, ${currentConfig.colors.background.color1} ${currentConfig.colors.background.stop1}%, ${currentConfig.colors.background.color2} ${currentConfig.colors.background.stop2}%)`
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: '#64a6ff', mb: 2 }}>2. Bouton 1</Typography>
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={4}>
                                    <TextField label="Texte" fullWidth size="small" value={currentConfig.colors.filledButton.text} onChange={(e) => handleChange(['colors', 'filledButton', 'text'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.filledButton.text} onChange={(e) => handleChange(['colors', 'filledButton', 'text'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField label="Fond" fullWidth size="small" value={currentConfig.colors.filledButton.background} onChange={(e) => handleChange(['colors', 'filledButton', 'background'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.filledButton.background} onChange={(e) => handleChange(['colors', 'filledButton', 'background'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField label="Bordure" fullWidth size="small" value={currentConfig.colors.filledButton.border} onChange={(e) => handleChange(['colors', 'filledButton', 'border'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.filledButton.border} onChange={(e) => handleChange(['colors', 'filledButton', 'border'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button variant="contained" sx={{
                                        bgcolor: currentConfig.colors.filledButton.background,
                                        color: currentConfig.colors.filledButton.text,
                                        border: `1px solid ${currentConfig.colors.filledButton.border}`,
                                        boxShadow: 'none'
                                    }}>Aperçu Bouton 1</Button>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: '#64a6ff', mb: 2 }}>3. Bouton 2</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <TextField label="Texte" fullWidth size="small" value={currentConfig.colors.outlinedButton.text} onChange={(e) => handleChange(['colors', 'outlinedButton', 'text'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.outlinedButton.text} onChange={(e) => handleChange(['colors', 'outlinedButton', 'text'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField label="Fond" fullWidth size="small" value={currentConfig.colors.outlinedButton.background} onChange={(e) => handleChange(['colors', 'outlinedButton', 'background'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.outlinedButton.background} onChange={(e) => handleChange(['colors', 'outlinedButton', 'background'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField label="Bordure" fullWidth size="small" value={currentConfig.colors.outlinedButton.border} onChange={(e) => handleChange(['colors', 'outlinedButton', 'border'], e.target.value)} InputProps={{ startAdornment: <input type="color" value={currentConfig.colors.outlinedButton.border} onChange={(e) => handleChange(['colors', 'outlinedButton', 'border'], e.target.value)} style={{ width: 20, height: 20, border: 'none', marginRight: 8 }} /> }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button variant="outlined" sx={{
                                        bgcolor: currentConfig.colors.outlinedButton.background,
                                        color: currentConfig.colors.outlinedButton.text,
                                        border: `1px solid ${currentConfig.colors.outlinedButton.border}`,
                                        boxShadow: 'none'
                                    }}>Aperçu Bouton 2</Button>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ color: '#64a6ff' }}>4. Effets 3D (Three.js)</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentConfig.threeJs?.enabled ?? true}
                                            onChange={(e) => handleChange(['threeJs', 'enabled'], e.target.checked)}
                                        />
                                    }
                                    label="Activer la 3D"
                                    sx={{ color: 'white' }}
                                />
                            </Box>

                            {currentConfig.threeJs?.enabled && (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                            <InputLabel id="threejs-style-label">Style de rendu</InputLabel>
                                            <Select
                                                labelId="threejs-style-label"
                                                value={currentConfig.threeJs?.style || 'both'}
                                                label="Style de rendu"
                                                onChange={(e) => handleChange(['threeJs', 'style'], e.target.value)}
                                            >
                                                <MenuItem value="both">Grille et Particules</MenuItem>
                                                <MenuItem value="grid">Grille ondulante uniquement</MenuItem>
                                                <MenuItem value="particles">Particules uniquement</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
                                            Vitesse des vagues : {currentConfig.threeJs?.waveSpeed ?? 0.5}
                                        </Typography>
                                        <Slider
                                            value={currentConfig.threeJs?.waveSpeed ?? 0.5}
                                            min={0}
                                            max={2}
                                            step={0.1}
                                            onChange={(_, v) => handleChange(['threeJs', 'waveSpeed'], v)}
                                            valueLabelDisplay="auto"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
                                            Nombre de particules : {currentConfig.threeJs?.particleCount ?? 800}
                                        </Typography>
                                        <Slider
                                            value={currentConfig.threeJs?.particleCount ?? 800}
                                            min={100}
                                            max={2000}
                                            step={50}
                                            onChange={(_, v) => handleChange(['threeJs', 'particleCount'], v)}
                                            valueLabelDisplay="auto"
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            <Box sx={{ mt: 4 }}>
                                <Button variant="contained" fullWidth size="large" onClick={handleSave} disabled={saving}>
                                    {saving ? "Sauvegarde..." : "Sauvegarder ce thème"}
                                </Button>
                            </Box>

                        </Paper>
                    </Grid>

                    {/* DROITE : IMAGES */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, backgroundColor: 'rgba(23, 42, 69, 0.9)', color: 'white', height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 3, color: '#64a6ff' }}>Images & Assets</Typography>

                            {/* LOGOS */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Logos (Tirage au sort si plusieurs)</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    {currentConfig.assets?.logos?.map((logo, i) => (
                                        <Box key={i} sx={{ position: 'relative', width: 60, height: 60, border: '1px solid #555', borderRadius: 1, overflow: 'hidden' }}>
                                            <img src={logo.url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            <IconButton size="small" onClick={() => removeAsset(logo, 'logo')} sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', p: 0.5 }}>
                                                <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                                <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}>
                                    Ajouter Logo
                                    <input type="file" hidden accept="image/*" onChange={(e) => e.target.files && handleAssetUpload(e.target.files[0], 'logo')} />
                                </Button>
                            </Box>

                            {/* VISEUR */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2">Viseur (Cible mouvante)</Typography>
                                    <FormControlLabel control={<Switch size="small" checked={currentConfig.assets?.cursorEnabled || false} onChange={(e) => handleChange(['assets', 'cursorEnabled'], e.target.checked)} />} label="Activer" />
                                </Box>

                                {currentConfig.assets?.cursorEnabled && (
                                    <>
                                        <Box sx={{ mt: 1, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ width: 50, height: 50, border: '1px dashed gray', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {currentConfig.assets?.cursor ? (
                                                    <img src={currentConfig.assets.cursor.url} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                                ) : <Typography variant="caption">Aucun</Typography>}
                                            </Box>
                                            <Button component="label" size="small" variant="outlined">
                                                Changer
                                                <input type="file" hidden accept="image/*, .cur" onChange={(e) => e.target.files && handleAssetUpload(e.target.files[0], 'cursor')} />
                                            </Button>
                                            {currentConfig.assets?.cursor && <IconButton onClick={() => removeAsset(currentConfig.assets!.cursor!, 'cursor')}><DeleteIcon /></IconButton>}
                                        </Box>

                                        {/* IMPACTS (Sous-section du viseur) */}
                                        <Box sx={{ pl: 2, borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                                            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#aaa' }}>Impacts (Aléatoire parmi la liste)</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                {currentConfig.assets?.impacts?.map((impact, i) => (
                                                    <Box key={i} sx={{ position: 'relative', width: 50, height: 50, border: '1px solid #555', borderRadius: 1, overflow: 'hidden' }}>
                                                        <img src={impact.url} alt="impact" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                        <IconButton size="small" onClick={() => removeAsset(impact, 'impact')} sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', p: 0.25 }}>
                                                            <CloseIcon fontSize="small" sx={{ fontSize: '1rem', color: 'white' }} />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}>
                                                Ajouter Impact
                                                <input type="file" hidden accept="image/*" onChange={(e) => e.target.files && handleAssetUpload(e.target.files[0], 'impact')} />
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                            {/* FILIGRANE */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle2">Filigrane (Fond de page)</Typography>
                                    <FormControlLabel control={<Switch size="small" checked={currentConfig.assets?.watermarkEnabled || false} onChange={(e) => handleToggleWatermark(e.target.checked)} />} label="Activer" />
                                </Box>

                                {currentConfig.assets?.watermarkEnabled && (
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ width: 80, height: 80, border: '1px dashed gray', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            {currentConfig.assets?.watermark ? (
                                                <img src={currentConfig.assets.watermark.url} style={{ width: '100%', opacity: 0.5 }} />
                                            ) : <Typography variant="caption">Aucun</Typography>}
                                        </Box>
                                        <Button component="label" size="small" variant="outlined">
                                            Changer
                                            <input type="file" hidden accept="image/*" onChange={(e) => e.target.files && handleAssetUpload(e.target.files[0], 'watermark')} />
                                        </Button>
                                        {currentConfig.assets?.watermark && <IconButton onClick={() => removeAsset(currentConfig.assets!.watermark!, 'watermark')}><DeleteIcon /></IconButton>}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                </Grid>

                <Snackbar open={!!message} autoHideDuration={6000} onClose={() => setMessage(null)}>
                    <Alert severity={message?.type || 'info'} onClose={() => setMessage(null)}>{message?.text}</Alert>
                </Snackbar>
            </Container>
        </PageLayout>
    );
};

export default ThemeConfig;
